import type {
    ForwardRefExoticComponent,
    MemoExoticComponent,
    RefAttributes,
    SVGProps,
} from "react"
import React from "react"
import { t } from "@lingui/core/macro"
import { SendArrow, Tokens } from "../../../common/atoms/Icons/Chat"
import { ClickableTooltip } from "../../../common/molecules/ClickableTooltip"
import { Placement } from "../../../common/molecules/ClickableTooltip/ClickableTooltip"
import { IconButton } from "../../common/IconButton/IconButton"
import { useMessagingView } from "../../hooks"
import { classNames } from "../../utils"
import styles from "./SubmitFormIcon.module.scss"

const BTN_ID_SUBMIT = "submit-btn"
const BTN_ID_TOKENS = "tokens-btn"
const BTN_ID_TOKENS_GREY = "tokens-grey-btn"
const SUB_ID_SUBMIT = "submit-icon"
const SUB_ID_TOKENS = "tokens-icon"
const SUB_ID_TOKENS_GREY = "tokens-grey-icon"

type Props = {
    isMobile: boolean
    isLoading: boolean
    canSend: boolean
    canTip: boolean
    handleSendMessage: () => void
    handleSendTokens: () => void
}

enum DisplayCases {
    Unkonwn,
    LoadingCantSend,
    LoadingCanSend,
    NotLoadingCanSend,
    NotLoadingCantSendCanTip,
    NotLoadingCantSendCantTip,
}

interface IButtonConfigProps {
    butonId: string
    iconAlt: string
    subId: string
    // TODO: https://multimediallc.atlassian.net/browse/MSG-461 - this will let us replace "any" with a specific interface
    subType: MemoExoticComponent<
        ForwardRefExoticComponent<
            /* eslint-disable @typescript-eslint/no-redundant-type-constituents */
            Omit<SVGProps<SVGSVGElement> & any, "ref"> &
                RefAttributes<SVGSVGElement>
        >
    >
    subStyles?: string
    tooltipText?: string
    onClick?: (e: React.MouseEvent) => void
}

/*
 * Helper for handling the submit icon in the MessageInputBar.
 * Cases to handle in order:
 * 1. Loading, cannot send (no text) - disabled token icon
 * 2. Loading, can send (has text) - disabled send icon w/ tooltip
 * 3. Not loading, can send - enabled send icon
 * 4. Not loading, cannot send, can tip - enabled token icon
 * 5. Not loading, cannot send, cannot tip - disabled token icon w/ tooltip
 */
export function SubmitFormIcon({
    isMobile,
    isLoading,
    canSend,
    canTip,
    handleSendMessage,
    handleSendTokens,
}: Props) {
    const ALT_SEND_TOKENS = t`Send tokens`
    const ALT_SEND_MESSAGE = t`Send message`

    const TOOLTIP_DISABLED_TOKENS = t`This user may not receive tokens.`
    const TOOLTIP_LOADING_MESSAGE = t`Please wait — Messages are still loading.`

    const messagingView = useMessagingView()
    const isDesktop = !messagingView.isMobile

    const swallowClickEvent = (e: React.MouseEvent) => {
        e.preventDefault()
    }

    // Initialize the different case configurations.
    const icon_config: { [id: string]: IButtonConfigProps } = {
        [DisplayCases.Unkonwn]: {
            // Defaults to the first known case.
            butonId: BTN_ID_TOKENS,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS_GREY,
            subStyles: classNames(styles.tokens, styles.disabled),
            onClick: swallowClickEvent,
        },
        [DisplayCases.LoadingCantSend]: {
            butonId: BTN_ID_TOKENS,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS_GREY,
            subStyles: classNames(styles.tokens, styles.disabled),
            onClick: swallowClickEvent,
        },
        [DisplayCases.LoadingCanSend]: {
            butonId: BTN_ID_SUBMIT,
            iconAlt: ALT_SEND_MESSAGE,
            subType: SendArrow,
            subId: SUB_ID_SUBMIT,
            subStyles: classNames(styles.arrow, styles.disabled),
            tooltipText: TOOLTIP_LOADING_MESSAGE,
            onClick: swallowClickEvent,
        },
        [DisplayCases.NotLoadingCanSend]: {
            butonId: BTN_ID_SUBMIT,
            iconAlt: ALT_SEND_MESSAGE,
            subType: SendArrow,
            subId: SUB_ID_SUBMIT,
            subStyles: classNames(styles.arrow),
            tooltipText: "",
            onClick: (e) => {
                swallowClickEvent(e)
                handleSendMessage()
            },
        },
        [DisplayCases.NotLoadingCantSendCanTip]: {
            butonId: BTN_ID_TOKENS,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS,
            subStyles: classNames(styles.tokens),
            tooltipText: "",
            onClick: (e) => {
                swallowClickEvent(e)
                handleSendTokens()
            },
        },
        [DisplayCases.NotLoadingCantSendCantTip]: {
            butonId: BTN_ID_TOKENS_GREY,
            iconAlt: ALT_SEND_TOKENS,
            subType: Tokens,
            subId: SUB_ID_TOKENS,
            subStyles: classNames(styles.tokens, styles.disabled),
            tooltipText: TOOLTIP_DISABLED_TOKENS,
            onClick: (e) => {
                swallowClickEvent(e)
                handleSendTokens() // Used for NewRelic event tracking of disabled token icon
            },
        },
    }

    // Determine which condition to select based on isLoading, canSend, and canTip.
    let condition: DisplayCases
    if (isLoading && !canSend) {
        condition = DisplayCases.LoadingCantSend
    } else if (isLoading && canSend) {
        condition = DisplayCases.LoadingCanSend
    } else if (!isLoading && canSend) {
        condition = DisplayCases.NotLoadingCanSend
    } else if (!isLoading && !canSend && canTip) {
        condition = DisplayCases.NotLoadingCantSendCanTip
    } else if (!isLoading && !canSend && !canTip) {
        condition = DisplayCases.NotLoadingCantSendCantTip
    } else {
        condition = DisplayCases.Unkonwn
    }
    const config = icon_config[condition]

    const ttProps = isMobile
        ? {
              clickToOpen: true,
              placement: Placement.top,
          }
        : {
              clickToOpen: false,
              placement: Placement.topMiddle,
          }

    return (
        <ClickableTooltip
            clickToOpen={ttProps.clickToOpen}
            text={config.tooltipText}
            toolTipClassName={classNames(styles.tooltipOverride, {
                [styles.mobile]: isMobile,
                [styles.desktop]: !isMobile,
            })}
            placement={ttProps.placement}
        >
            <IconButton
                data-testid={config.butonId}
                className={styles.iconButton}
                icon={React.createElement(config.subType, {
                    "data-testid": config.subId,
                    className: classNames(config.subStyles, {
                        [styles.desktop]: isDesktop,
                    }),
                })}
                onMouseDown={config.onClick}
                aria-label={config.iconAlt}
            />
        </ClickableTooltip>
    )
}
